import useSWR from 'swr';
import { resolveCommendaAiAttachmentViewUrl } from './api';

/** A URL the browser can render directly (already-signed S3, blob, or data). Bare private S3 paths
 * (just-uploaded, not-yet-signed) are not, so those get resolved via the view endpoint. */
function isDirectlyViewable(url: string): boolean {
  return (
    url.startsWith('blob:') || url.startsWith('data:') || url.includes('X-Amz-')
  );
}

/** Resolve a browser-viewable URL for an attachment reference. If the ref is already viewable it's
 * returned as-is (no request); otherwise a fresh inline URL is fetched on demand. Pass `enabled: false`
 * to skip resolution entirely (e.g. a non-image card that doesn't need a thumbnail). */
export function useAttachmentViewUrl(
  fileRef: string | undefined,
  enabled = true
): { url: string | undefined; isLoading: boolean; error: Error | undefined } {
  const direct = fileRef && isDirectlyViewable(fileRef) ? fileRef : undefined;
  const shouldFetch = enabled && Boolean(fileRef) && !direct;

  const { data, error, isLoading } = useSWR(
    shouldFetch ? ['commenda-ai/attachment-view-url', fileRef] : null,
    () => resolveCommendaAiAttachmentViewUrl(fileRef as string)
  );

  return {
    url: direct ?? data,
    isLoading: shouldFetch && isLoading,
    error,
  };
}
